/**
 * At least on of following props should be used: Model or modelInstance
 * If Model prop is passed: will be created instance of model and async search used
 * If modelInstance props is passed: will be sync search of modelInstance.data used
 */
import { observer } from 'mobx-react';
import React from 'react';

import { IAnyModelType, clone, getSnapshot } from 'mobx-state-tree';

import useModel from '@common/helpers/useModel';

import SearchSelect, { SearchSelectProps } from '.';

// @TODO Better types. Move RequireAtLeastOne to helpers
type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
	{
		[K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
	}[Keys];

interface SearchModelSelectProps extends Omit<SearchSelectProps, 'loading' | 'data' | 'onSearch'> {
	Model?: IAnyModelType;
	modelInstance?: any; // @TODO: Better naming
	itemModel?: IAnyModelType;
	initial?: any;
	size?: 'small' | 'medium';
}

export type SearchModelFieldProps = RequireAtLeastOne<
	SearchModelSelectProps,
	'modelInstance' | 'Model'
>;

function SearchModelSelect({
	Model,
	modelInstance,
	itemModel,
	initial = {},
	value,
	onChange,
	...props
}: SearchModelFieldProps) {
	const model = modelInstance || useModel(Model, initial, Object.keys(initial));
	const prepareValue = (value: any) => {
		if (Array.isArray(value)) {
			return value.map((option: any) => itemModel.create(option));
		}
		return value ? itemModel.create(value) : value;
	};
	const prepareNewValue = (newValue: any) => {
		if (Array.isArray(newValue)) {
			return newValue.map((option: any) => getSnapshot(option));
		}
		return newValue ? getSnapshot(newValue) : newValue;
	};

	return (
		<SearchSelect
			{...props}
			value={itemModel ? prepareValue(value) : value}
			onChange={(newValue) => onChange(itemModel ? prepareNewValue(newValue) : newValue)}
			loading={model.loading}
			data={model.data.map((option: IAnyModelType) => clone(option))}
			onSearch={
				!modelInstance
					? (search: string) => {
							model.setSearch(search);
							model.fetch();
						}
					: undefined
			}
		/>
	);
}

export default observer(SearchModelSelect);
